import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Formik } from "formik";
import * as Yup from "yup";
import { Link, useHistory, useParams } from "react-router-dom";

import Layout from "../../../../components/Layout";
import Container from "../../../../components/Container";
import Flex from "../../../../components/Flex";
import Content from "../../../../components/Content";
import Button from "../../../../components/Button";
import ButtonList from "../../../../components/ButtonList";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import { useMessageToast } from "../../../../components/MessageProvider";
import { useFetch } from "../../../../components/useFetch";
import { useAuthState } from "../../../../components/AuthProvider";
import Label from "../../../../components/Label";
import TextBox from "../../../../components/TextBox";
import Icon from "../../../../components/Icon";
import Spinner from "../../../../components/Loader/Spinner";

import styles from "./AddEditUser.module.scss";

const AddEditUser = () => {
  const moment = require("moment-timezone");
  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  const userApiUrl = `${clientConfigApiDomain}/users`;

  const { id } = useParams();

  const history = useHistory();
  const { user } = useAuthState();
  const toast = useMessageToast();

  const [
    {
      data: addEditUserData,
      hasLoaded: addEditHasLoaded,
      hasError: addEditHasError
    },
    addEditFetch
  ] = useFetch();

  const [userData, setUserData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const fetchData = useCallback(async () => {
    window.scrollTo(0, 0);
    setIsLoading(true);
    const response = await fetch(`${userApiUrl}/${id}/`);
    const data = await response.json();
    setUserData(data);
    setIsLoading(false);
    setHasLoaded(true);
  }, [id, userApiUrl]);

  useEffect(() => {
    if (!hasLoaded && id !== undefined) {
      fetchData();
    } else {
      // this enables the add screen to show up
      setHasLoaded(true);
    }
  }, [id, fetchData, hasLoaded]);

  useEffect(() => {
    if (id === undefined && addEditHasLoaded) {
      if (addEditHasLoaded) {
        toast.success(`User added successfully.`);
        if (user.role === "Global Admin") {
          history.push(`/admin/users/${addEditUserData.id}/access/add`);
        } else {
          history.push(`/admin/users/${addEditUserData.id}`);
        }
      } else if (addEditHasError) {
        toast.error(`Failed to add user.`);
      }
    } else {
      if (addEditHasLoaded) {
        toast.success(`User updated successfully.`);

        history.push(`/admin/users/${addEditUserData.id}`);
      } else if (addEditHasError) {
        toast.error(`Failed to update this user.`);
      }
    }
  }, [id, addEditUserData, addEditHasLoaded, addEditHasError, toast, history]);

  let loadedBreadcrumbs = "";
  if (isLoading) {
    loadedBreadcrumbs = <Spinner />;
  } else if (hasLoaded) {
    loadedBreadcrumbs = (
      <Breadcrumbs>
        <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
        <Breadcrumbs.Item
          render={() => <Link to="/admin">Administration</Link>}
        />
        <Breadcrumbs.Item render={() => <Link to="/admin/users">Users</Link>} />
        {id ? (
          <>
            <Breadcrumbs.Item
              render={() => (
                <Link to={`/admin/users/${id}`}>
                  User: {userData.last_name}, {userData.first_name}
                </Link>
              )}
            />
            <Breadcrumbs.Item last render={() => "Edit"}></Breadcrumbs.Item>
          </>
        ) : (
          <Breadcrumbs.Item last render={() => `Add User`} />
        )}
      </Breadcrumbs>
    );
  }

  const isEditVisible = selectedUser => {
    if (userData.length !== 0) {
      const selectedUserRoles = [
        ...new Set(selectedUser.roles.map(role => role.name))
      ];
      return (
        user.role === "Global Admin" ||
        !selectedUserRoles.includes("Global Admin")
      );
    }
    return true;
  };

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item span={12}>{loadedBreadcrumbs}</Flex.Item>
          {isLoading ? (
            <Spinner />
          ) : hasLoaded ? (
            <Flex.Item span={12}>
              <Content>
                {id ? (
                  <h1>
                    Edit User: {userData.last_name}, {userData.first_name}
                  </h1>
                ) : (
                  <h1>Add User</h1>
                )}
              </Content>
              <div className={styles.base}>
                <div className={styles.detailsFlex}>
                  <h3>User Details</h3>
                </div>
                <div className={styles.details}>
                  <Formik
                    initialValues={{
                      id: userData.id,
                      firstName: userData.first_name,
                      lastName: userData.last_name,
                      email: userData.email,
                      createdBy: userData.created_by,
                      updatedBy: userData.updated_by
                    }}
                    validationSchema={Yup.object().shape({
                      firstName: Yup.string()
                        .trim()
                        .required("First Name field is required.")
                        .matches(
                          /^(?![=@+\-\r\n\t])(?:(?![,;:"'][=@+\-\r\n\t]).)*$/,
                          "Invalid characters pattern found in firstname"
                        )
                        .min(
                          2,
                          "First Name name should be at-least two characters."
                        )
                        .max(
                          100,
                          "First Name name should be max 100 characters long."
                        ),
                      lastName: Yup.string()
                        .trim()
                        .required("Last Name field is required.")
                        .matches(
                          /^(?![=@+\-\r\n\t])(?:(?![,;:"'][=@+\-\r\n\t]).)*$/,
                          "Invalid characters pattern found in lastname"
                        )
                        .min(
                          2,
                          "Last Name name should be at-least two characters."
                        )
                        .max(
                          120,
                          "First Name name should be max 120 characters long."
                        ),
                      email: Yup.string()
                        .trim()
                        .email("Email is Invalid")
                        .required("Email field is required.")
                        .max(
                          100,
                          "First Name name should be max 100 characters long."
                        )
                        .test("Unique Email", "Email already in use", function(
                          value
                        ) {
                          if (
                            value !== undefined &&
                            /^[a-zA-Z0-9]+([._-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+([._-][a-zA-Z0-9]+)*(\.[a-zA-Z]{2,3})+$/.test(
                              value
                            )
                          ) {
                            return new Promise((resolve, reject) => {
                              axios
                                .get(`${userApiUrl}/?email=${value}`)
                                .then(res => {
                                  if (
                                    (id === undefined && res.status === 204) ||
                                    parseInt(id) ===
                                      parseInt(res.data.results[0].id)
                                  ) {
                                    resolve(true);
                                  }
                                  resolve(false);
                                });
                            });
                          }
                        })
                    })}
                    onSubmit={(values, { setSubmitting, resetForm }) => {
                      setSubmitting(true);
                      let data = {
                        first_name: values.firstName,
                        last_name: values.lastName,
                        email: values.email,
                        updated_by: user.username,
                        created_by: values.createdBy
                      };
                      let method = "";
                      let url = "";

                      if (id) {
                        data["id"] = id;
                        method = "put";
                        url = `${userApiUrl}/${id}/`;
                      } else {
                        data["created_by"] = user.username;
                        method = "post";
                        url = `${userApiUrl}/`;
                      }

                      addEditFetch({
                        method,
                        url,
                        data
                      });

                      setSubmitting(false);
                    }}
                  >
                    {props => {
                      const {
                        values,
                        touched,
                        errors,
                        handleChange,
                        handleBlur,
                        handleSubmit
                      } = props;

                      return (
                        <form onSubmit={handleSubmit} autoComplete="off">
                          <div>
                            {id ? (
                              <>
                                <h4>User ID</h4>
                                <span>{userData.id}</span>
                              </>
                            ) : null}
                          </div>
                          <div>
                            <TextBox
                              errorText={
                                touched.firstName ? errors.firstName : null
                              }
                              id="firstName"
                              label={
                                <Label
                                  error={touched.firstName && errors.firstName}
                                  htmlFor="firstName"
                                >
                                  <b>First Name</b>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                              value={values.firstName}
                              width="33%"
                            />
                          </div>
                          <div>
                            <TextBox
                              errorText={
                                touched.lastName ? errors.lastName : null
                              }
                              id="lastName"
                              label={
                                <Label
                                  error={touched.lastName && errors.lastName}
                                  htmlFor="lastName"
                                >
                                  <b>Last Name</b>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                              value={values.lastName}
                              width="33%"
                            />
                          </div>
                          <div>
                            {id ? (
                              <>
                                <h4>Email</h4>
                                <span>{userData.email}</span>
                              </>
                            ) : (
                              <TextBox
                                errorText={touched.email ? errors.email : null}
                                id="email"
                                label={
                                  <Label
                                    error={touched.email && errors.email}
                                    htmlFor="lastName"
                                  >
                                    <b>Email</b>
                                  </Label>
                                }
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                                value={values.email}
                                width="33%"
                              />
                            )}
                          </div>
                          {id ? (
                            <>
                              <div>
                                <h4>Created By</h4>
                                <span>{userData.created_by}</span>
                              </div>
                              <div>
                                <h4>Date Created</h4>
                                <span>
                                  {moment(userData.created_at)
                                    .tz(user.preferences.timeZone)
                                    .format("L, LTS z")}
                                </span>
                              </div>
                              <div>
                                <h4>Edited By</h4>
                                <span>{userData.updated_by}</span>
                              </div>
                              <div>
                                <h4>Date Updated</h4>
                                <span>
                                  {moment(userData.updated_at)
                                    .tz(user.preferences.timeZone)
                                    .format("L, LTS z")}
                                </span>
                              </div>
                            </>
                          ) : null}

                          <ButtonList>
                            <Button
                              primary
                              type="submit"
                              disabled={!isEditVisible(userData)}
                            >
                              {id ? (
                                "Save Changes"
                              ) : (
                                <>
                                  {user.role === "Organization Admin" &&
                                  window.localStorage.getItem("LOGIN_TYPE") ===
                                    "MS-Email"
                                    ? "Add"
                                    : "Add Roles"}
                                  <Icon type="arrow_right" />
                                </>
                              )}
                            </Button>
                            <Button
                              onClick={() => {
                                history.push(`/admin/users`);
                              }}
                            >
                              Cancel
                            </Button>
                          </ButtonList>
                        </form>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </Flex.Item>
          ) : null}
        </Flex>
      </Container>
    </Layout>
  );
};

export default AddEditUser;
